/**
 * Client-side catalog for thread groups.
 *
 * Group definitions (id, name, colorId) live in the thread-groups store (localStorage today,
 * see TODO in src/lib/stores/thread-groups-store.js). The selected group for each thread is
 * persisted server-side as `thread.metadata.group = "<groupId>"` via PUT /api/admin/threads/{id}.
 *
 * Palette is fixed and Tailwind-class-based so chips render with consistent contrast in
 * light & dark mode (mirrors the flag chip pattern in src/lib/thread-flags.js).
 */

export const GROUP_COLOR_PALETTE = [
  {
    id: "violet",
    label: "Violet",
    swatchClassName: "bg-violet-500",
    chipClassName:
      "border border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-500/40 dark:bg-violet-950/60 dark:text-violet-100",
    sectionClassName:
      "bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:text-violet-100",
    dotClassName: "bg-violet-500",
  },
  {
    id: "rose",
    label: "Rose",
    swatchClassName: "bg-rose-500",
    chipClassName:
      "border border-rose-300 bg-rose-100 text-rose-900 dark:border-rose-500/40 dark:bg-rose-950/60 dark:text-rose-100",
    sectionClassName:
      "bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-100",
    dotClassName: "bg-rose-500",
  },
  {
    id: "amber",
    label: "Amber",
    swatchClassName: "bg-amber-500",
    chipClassName:
      "border border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/60 dark:text-amber-100",
    sectionClassName:
      "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
    dotClassName: "bg-amber-500",
  },
  {
    id: "emerald",
    label: "Emerald",
    swatchClassName: "bg-emerald-500",
    chipClassName:
      "border border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100",
    sectionClassName:
      "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100",
    dotClassName: "bg-emerald-500",
  },
  {
    id: "teal",
    label: "Teal",
    swatchClassName: "bg-teal-500",
    chipClassName:
      "border border-teal-300 bg-teal-100 text-teal-900 dark:border-teal-500/40 dark:bg-teal-950/60 dark:text-teal-100",
    sectionClassName:
      "bg-teal-50 text-teal-900 dark:bg-teal-950/40 dark:text-teal-100",
    dotClassName: "bg-teal-500",
  },
  {
    id: "sky",
    label: "Sky",
    swatchClassName: "bg-sky-500",
    chipClassName:
      "border border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-500/40 dark:bg-sky-950/60 dark:text-sky-100",
    sectionClassName:
      "bg-sky-50 text-sky-900 dark:bg-sky-950/40 dark:text-sky-100",
    dotClassName: "bg-sky-500",
  },
  {
    id: "blue",
    label: "Blue",
    swatchClassName: "bg-blue-500",
    chipClassName:
      "border border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-500/40 dark:bg-blue-950/60 dark:text-blue-100",
    sectionClassName:
      "bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-100",
    dotClassName: "bg-blue-500",
  },
  {
    id: "fuchsia",
    label: "Fuchsia",
    swatchClassName: "bg-fuchsia-500",
    chipClassName:
      "border border-fuchsia-300 bg-fuchsia-100 text-fuchsia-900 dark:border-fuchsia-500/40 dark:bg-fuchsia-950/60 dark:text-fuchsia-100",
    sectionClassName:
      "bg-fuchsia-50 text-fuchsia-900 dark:bg-fuchsia-950/40 dark:text-fuchsia-100",
    dotClassName: "bg-fuchsia-500",
  },
  {
    id: "orange",
    label: "Orange",
    swatchClassName: "bg-orange-500",
    chipClassName:
      "border border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-500/40 dark:bg-orange-950/60 dark:text-orange-100",
    sectionClassName:
      "bg-orange-50 text-orange-900 dark:bg-orange-950/40 dark:text-orange-100",
    dotClassName: "bg-orange-500",
  },
  {
    id: "slate",
    label: "Slate",
    swatchClassName: "bg-slate-500",
    chipClassName:
      "border border-slate-300 bg-slate-100 text-slate-900 dark:border-slate-500/40 dark:bg-slate-900/60 dark:text-slate-100",
    sectionClassName:
      "bg-slate-100 text-slate-900 dark:bg-slate-900/50 dark:text-slate-100",
    dotClassName: "bg-slate-500",
  },
];

const FALLBACK_GROUP_COLOR = GROUP_COLOR_PALETTE[GROUP_COLOR_PALETTE.length - 1];

const paletteById = Object.fromEntries(GROUP_COLOR_PALETTE.map((entry) => [entry.id, entry]));

export function getGroupColor(colorId) {
  const id = String(colorId || "").trim();
  return paletteById[id] || FALLBACK_GROUP_COLOR;
}

export const DEFAULT_THREAD_GROUPS = [
  { id: "triage", name: "Triage", colorId: "rose" },
  { id: "follow-up", name: "Follow-up", colorId: "emerald" },
  { id: "vip", name: "VIP", colorId: "violet" },
];

// Group behavior flags (whitelisted; mirrored by the backend normalizer). "ignore" makes
// the bot leave the group's threads alone entirely — no replies, no locks, no auto tasks.
export const THREAD_GROUP_FLAG_IGNORE = "ignore";

export const THREAD_GROUP_FLAGS = [
  {
    id: THREAD_GROUP_FLAG_IGNORE,
    label: "Ignored by bot",
    description:
      "The assistant never replies to, locks, or creates tasks for threads in this group.",
  },
];

export function getThreadGroupFlags(group) {
  if (!Array.isArray(group?.flags)) return [];
  return group.flags.filter((f) => typeof f === "string" && f.trim());
}

export function groupHasFlag(group, flagId) {
  const key = String(flagId || "").trim();
  if (!key) return false;
  return getThreadGroupFlags(group).some((f) => f.trim() === key);
}

export function getThreadGroupId(thread) {
  const raw = thread?.metadata?.group;
  return typeof raw === "string" ? raw.trim() : "";
}

export function findGroupById(groups, id) {
  const key = String(id || "").trim();
  if (!key || !Array.isArray(groups)) return null;
  return groups.find((g) => g && g.id === key) || null;
}

export function slugifyGroupId(name) {
  const base = String(name || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return base || "group";
}

export function nextUnusedColorId(groups) {
  const used = new Set((Array.isArray(groups) ? groups : []).map((g) => g?.colorId).filter(Boolean));
  const free = GROUP_COLOR_PALETTE.find((c) => !used.has(c.id));
  return (free || GROUP_COLOR_PALETTE[0]).id;
}
